import { useEffect, useMemo, useCallback } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Row, Col, FormGroup, Label } from 'reactstrap';
import { REPORT_STATUS, REPORT_STATUS_ORDER } from '../Helpers/format';

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, { isDisabled, isFocused }) => ({
    ...base,
    minHeight: 38,
    borderColor: isFocused ? '#0e578e' : '#e9e9ef',
    boxShadow: isFocused ? '0 0 0 0.15rem rgba(14, 87, 142, 0.25)' : 'none',
    backgroundColor: isDisabled ? '#f8f9fa' : base.backgroundColor,
    cursor: isDisabled ? 'not-allowed' : 'default',
    '&:hover': { borderColor: isFocused ? '#0e578e' : '#ced4da' },
  }),
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'issues_found', label: 'Issues Found' },
  ...REPORT_STATUS_ORDER.map((s) => ({ value: s, label: REPORT_STATUS[s].label })),
];

/**
 * Property, building and status filters with unit search.
 *
 * filters: { property_id, building_name, status }
 */
const PropertySelector = ({ propertyList = [], filters, onChange, isLoading = false, searchControl }) => {
  const currentProperty = useMemo(
    () => propertyList.find((p) => p._id === filters.property_id) || null,
    [propertyList, filters.property_id]
  );

  const buildingList = useMemo(
    () => (currentProperty?.building || []).filter((b) => b && b.buildingName),
    [currentProperty]
  );

  const propertyOptions = useMemo(
    () =>
      propertyList.map((p) => ({
        value: p._id,
        label: `${p.title}${p.location ? `, ${p.location}` : ''}`,
      })),
    [propertyList]
  );

  const buildingOptions = useMemo(
    () =>
      buildingList.map((b) => ({
        value: b.buildingName,
        label: `${b.buildingName}${b.type ? ` (${b.type})` : ''}`,
      })),
    [buildingList]
  );

  const selectedProperty = useMemo(
    () => propertyOptions.find((o) => o.value === filters.property_id) || null,
    [propertyOptions, filters.property_id]
  );
  const selectedBuilding = useMemo(
    () => buildingOptions.find((o) => o.value === filters.building_name) || null,
    [buildingOptions, filters.building_name]
  );
  const selectedStatus = useMemo(
    () => STATUS_OPTIONS.find((o) => o.value === (filters.status || 'all')) || STATUS_OPTIONS[0],
    [filters.status]
  );

  // A property with exactly one building: pick it automatically.
  useEffect(() => {
    if (filters.property_id && buildingList.length === 1 && !filters.building_name) {
      onChange({ ...filters, building_name: buildingList[0].buildingName });
    }
  }, [buildingList, filters, onChange]);

  const handleProperty = useCallback(
    (opt) => onChange({ ...filters, property_id: opt ? opt.value : '', building_name: '' }),
    [filters, onChange]
  );
  const handleBuilding = useCallback(
    (opt) => onChange({ ...filters, building_name: opt ? opt.value : '' }),
    [filters, onChange]
  );
  const handleStatus = useCallback(
    (opt) => onChange({ ...filters, status: opt ? opt.value : 'all' }),
    [filters, onChange]
  );

  return (
    <div className="pm-toolbar">
      <Row className={`g-3 align-items-end pm-filter-grid pm-filter-grid--single${buildingList.length ? " has-building" : ""}`}>
        <Col className="pm-filter-property">
          <FormGroup className="mb-0">
            <Label for="pm-filter-property" className="form-label">
              <FeatherIcon icon="home" size={13} className="me-1" /> Property
            </Label>
            <Select
              inputId="pm-filter-property"
              value={selectedProperty}
              onChange={handleProperty}
              options={propertyOptions}
              placeholder="Select property"
              isClearable
              isDisabled={isLoading}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </FormGroup>
        </Col>

        {buildingList.length > 0 && (
          <Col className="pm-filter-building">
            <FormGroup className="mb-0">
              <Label for="pm-filter-building" className="form-label">
                <FeatherIcon icon="grid" size={13} className="me-1" /> Building
              </Label>
              <Select
                inputId="pm-filter-building"
                value={selectedBuilding}
                onChange={handleBuilding}
                options={buildingOptions}
                placeholder="Select building"
                isClearable={buildingList.length > 1}
                isDisabled={isLoading || buildingList.length === 1}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>
          </Col>
        )}

        <Col className="pm-filter-status">
          <FormGroup className="mb-0">
            <Label for="pm-filter-status" className="form-label">
              <FeatherIcon icon="clock" size={13} className="me-1" /> Status
            </Label>
            <Select
              inputId="pm-filter-status"
              value={selectedStatus}
              onChange={handleStatus}
              options={STATUS_OPTIONS}
              isDisabled={isLoading}
              isSearchable={false}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </FormGroup>
        </Col>

        {searchControl && <Col className="pm-filter-search">{searchControl}</Col>}

      </Row>
    </div>
  );
};

export { PropertySelector };
export default PropertySelector;
